import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { DTOShopTab } from 'src/apis/dto/DTOShopTab';
import type { GemProduct } from 'src/types/Payments';

import { STICKY_TABS_HEIGHT } from './styles';
import type { SubscriptionProduct } from './SubscriptionCard';
import type { ProductSectionData } from './useShopData';

interface UseShopTabsOptions {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  gems: ProductSectionData<GemProduct> | null;
  subscriptions: ProductSectionData<SubscriptionProduct> | null;
  tabs: DTOShopTab[];
}

interface UseShopTabsReturn {
  visibleTabs: DTOShopTab[];
  activeTab: string;
  scrollToTab: (anchor: string) => void;
  setSectionRef: (name: string) => (el: HTMLDivElement | null) => void;
}

const useShopTabs = ({
  scrollContainerRef,
  gems,
  subscriptions,
  tabs,
}: UseShopTabsOptions): UseShopTabsReturn => {
  const visibleTabs = useMemo(() => {
    const anchors = new Set<string>();
    if (gems?.anchor) anchors.add(gems.anchor);
    if (subscriptions?.anchor) anchors.add(subscriptions.anchor);
    return tabs.filter((tab) => anchors.has(tab.anchor));
  }, [tabs, gems, subscriptions]);

  const anchoredSections = useMemo(() => {
    const sections: { name: string }[] = [];
    if (gems?.anchor) sections.push({ name: gems.anchor });
    if (subscriptions?.anchor) sections.push({ name: subscriptions.anchor });
    return sections;
  }, [gems, subscriptions]);

  const [activeTab, setActiveTab] = useState(visibleTabs[0]?.anchor ?? '');

  const sectionRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const sectionRefCallbacks = useRef<Map<string, (el: HTMLDivElement | null) => void>>(new Map());
  const isScrollingByTab = useRef(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => clearTimeout(scrollTimeoutRef.current);
  }, []);

  const setSectionRef = useCallback((name: string) => {
    let cb = sectionRefCallbacks.current.get(name);
    if (!cb) {
      cb = (el: HTMLDivElement | null) => {
        if (el) {
          sectionRefs.current.set(name, el);
        } else {
          sectionRefs.current.delete(name);
        }
      };
      sectionRefCallbacks.current.set(name, cb);
    }
    return cb;
  }, []);

  const scrollToTab = useCallback(
    (anchor: string) => {
      setActiveTab(anchor);
      isScrollingByTab.current = true;

      const scrollContainer = scrollContainerRef.current;
      const targetEl = sectionRefs.current.get(anchor);

      if (scrollContainer && targetEl) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const targetRect = targetEl.getBoundingClientRect();
        const scrollTop =
          scrollContainer.scrollTop + targetRect.top - containerRect.top - STICKY_TABS_HEIGHT;

        scrollContainer.scrollTo({ top: scrollTop, behavior: 'smooth' });
      }

      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingByTab.current = false;
      }, 500);
    },
    [scrollContainerRef]
  );

  useEffect(() => {
    if (anchoredSections.length < 2) return;

    const scrollContainer = scrollContainerRef.current;
    const lastSection = anchoredSections[anchoredSections.length - 1];
    const lastSectionEl = sectionRefs.current.get(lastSection.name);

    if (!scrollContainer || !lastSectionEl) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (isScrollingByTab.current) return;
        const lastAnchor = lastSection.name;
        const firstAnchor = anchoredSections[0].name;
        setActiveTab(entry.intersectionRatio >= 0.5 ? lastAnchor : firstAnchor);
      },
      {
        root: scrollContainer,
        rootMargin: `-${STICKY_TABS_HEIGHT}px 0px 0px 0px`,
        threshold: [0.5],
      }
    );

    observer.observe(lastSectionEl);

    return () => observer.disconnect();
  }, [scrollContainerRef, anchoredSections]);

  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.some((tab) => tab.anchor === activeTab)) {
      setActiveTab(visibleTabs[0].anchor);
    }
  }, [visibleTabs, activeTab]);

  return {
    visibleTabs,
    activeTab,
    scrollToTab,
    setSectionRef,
  };
};

export default useShopTabs;
